import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LaunchIcon from '@mui/icons-material/Launch';
import SettingsIcon from '@mui/icons-material/Settings';

const OnboardingSuccess = ({ successData }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyAllCredentials = async () => {
    const username = successData.username || successData.email;
    const credentials = `Dashboard URL: ${successData.dashboard_url}\nUsername: ${username}\nPassword: ${successData.password}`;
    await copyToClipboard(credentials, 'all');
  };

  // Get username for display (use username if available, otherwise email)
  const displayUsername = successData.username || successData.email;

  // Extract tenant identifier from dashboard URL
  const getTenantFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname;
      // Extract tenant from hostname (e.g., tenant.nasradius.com -> tenant)
      const parts = hostname.split('.');
      if (parts.length >= 3 && parts[1] === 'nasradius') {
        return parts[0];
      }
      return 'TENANT';
    } catch (e) {
      return 'TENANT';
    }
  };

  const tenant = getTenantFromUrl(successData.dashboard_url);

  return (
    <Box sx={{ mt: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <CheckCircleIcon sx={{ 
          fontSize: { xs: 60, sm: 70, md: 80 }, 
          color: '#19b594', 
          mb: { xs: 1.5, sm: 2 } 
        }} />
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 700,
            background: 'linear-gradient(90deg, #19b594, #0b74c1)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.5rem', sm: '1.9rem', md: '2.2rem', lg: '2.5rem' },
            mb: { xs: 1.5, sm: 2 },
            lineHeight: 1.2,
            px: { xs: 1, sm: 0 },
          }}
        >
          Account Created Successfully!
        </Typography>
        <Typography 
          variant="body1"
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            color: '#4A4A4A',
            fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem', lg: '1.1rem' },
            px: { xs: 1, sm: 0 },
            lineHeight: 1.6,
          }}
        >
          Your NasRadius account has been set up. Save these credentials securely.
        </Typography>
      </Box>

      <Paper 
        variant="outlined" 
        sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 }, 
          mb: { xs: 2, sm: 3 }, 
          bgcolor: '#f9f9f9',
          borderRadius: '8px',
          borderColor: '#e0e0e0',
        }}
      >
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            mb: { xs: 1.5, sm: 2 },
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            color: '#2C3E50',
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
          }}
        >
          Your Account Credentials
        </Typography>

        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="body2"
            sx={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              color: '#4A4A4A',
              fontWeight: 500,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              mb: 0.5,
            }}
            gutterBottom
          >
            Dashboard URL
          </Typography>
          <TextField
            fullWidth
            value={successData.dashboard_url}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              },
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                py: { xs: 1, sm: 1.25 },
              },
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => copyToClipboard(successData.dashboard_url, 'url')}
                  edge="end"
                  sx={{ color: '#0b74c1', p: { xs: 0.75, sm: 1 } }}
                  size="small"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
                <IconButton
                  component={Link}
                  href={successData.dashboard_url}
                  target="_blank"
                  edge="end"
                  sx={{ color: '#0b74c1', p: { xs: 0.75, sm: 1 } }}
                  size="small"
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            size="small"
          />
          {copiedField === 'url' && (
            <Typography 
              variant="caption"
              sx={{ 
                mt: 0.5, 
                display: 'block',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                color: '#19b594',
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              }}
            >
              Copied!
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="body2"
            sx={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              color: '#4A4A4A',
              fontWeight: 500,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              mb: 0.5,
            }}
            gutterBottom
          >
            Username
          </Typography>
          <TextField
            fullWidth
            value={displayUsername}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              },
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                py: { xs: 1, sm: 1.25 },
              },
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => copyToClipboard(displayUsername, 'username')}
                  edge="end"
                  sx={{ color: '#0b74c1', p: { xs: 0.75, sm: 1 } }}
                  size="small"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            size="small"
          />
          {copiedField === 'username' && (
            <Typography 
              variant="caption"
              sx={{ 
                mt: 0.5, 
                display: 'block',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                color: '#19b594',
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              }}
            >
              Copied!
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="body2"
            sx={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              color: '#4A4A4A',
              fontWeight: 500,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              mb: 0.5,
            }}
            gutterBottom
          >
            Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={successData.password}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              },
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                py: { xs: 1, sm: 1.25 },
              },
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: '#0b74c1', p: { xs: 0.75, sm: 1 } }}
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <IconButton
                  onClick={() => copyToClipboard(successData.password, 'password')}
                  edge="end"
                  sx={{ color: '#0b74c1', p: { xs: 0.75, sm: 1 } }}
                  size="small"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            size="small"
          />
          {copiedField === 'password' && (
            <Typography 
              variant="caption"
              sx={{ 
                mt: 0.5, 
                display: 'block',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                color: '#19b594',
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              }}
            >
              Copied!
            </Typography>
          )}
        </Box>
      </Paper>

      <Alert 
        severity="warning" 
        sx={{ 
          mb: { xs: 2, sm: 3 },
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
          borderRadius: '8px',
          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
        }}
      >
        <Typography 
          variant="body2"
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
          }}
        >
          <strong>Important:</strong> Please save these credentials in a secure location.
          You will need them to access your dashboard. The password cannot be recovered.
        </Typography>
      </Alert>

      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 1.5, sm: 2 }, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' },
      }}>
        <Button
          variant="outlined"
          onClick={copyAllCredentials}
          startIcon={<ContentCopyIcon />}
          fullWidth
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            borderRadius: '8px',
            borderColor: '#19b594',
            color: '#19b594',
            textTransform: 'none',
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
            maxWidth: { xs: '100%', sm: 'none' },
            '&:hover': {
              borderColor: '#17a085',
              backgroundColor: 'rgba(25, 181, 148, 0.04)',
            },
          }}
        >
          Copy All Credentials
        </Button>
        <Button
          variant="outlined"
          href={`/setup-mikrotik?tenant=${tenant}`}
          target="_blank"
          startIcon={<SettingsIcon />}
          fullWidth
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            borderRadius: '8px',
            borderColor: '#0b74c1',
            color: '#0b74c1',
            textTransform: 'none',
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
            maxWidth: { xs: '100%', sm: 'none' },
            '&:hover': {
              borderColor: '#0a66bb',
              backgroundColor: 'rgba(11, 116, 193, 0.04)',
            },
          }}
        >
          Configure Mikrotik
        </Button>
        <Button
          variant="contained"
          href={successData.dashboard_url}
          target="_blank"
          startIcon={<LaunchIcon />}
          size="large"
          fullWidth
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            borderRadius: '8px',
            background: 'linear-gradient(90deg, #0a66bb, #19b594)',
            color: '#fff',
            textTransform: 'none',
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            py: { xs: 1.25, sm: 1.5 },
            maxWidth: { xs: '100%', sm: 'none' },
            boxShadow: '0px 4px 12px rgba(11, 116, 193, 0.2)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(90deg, #0b74c1, #219cac)',
              transform: 'translateY(-2px)',
              boxShadow: '0px 6px 16px rgba(11, 116, 193, 0.3)',
            },
          }}
        >
          Go to Dashboard
        </Button>
      </Box>

      {copiedField === 'all' && (
        <Alert 
          severity="success" 
          sx={{ 
            mt: { xs: 1.5, sm: 2 },
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            borderRadius: '8px',
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          }}
        >
          All credentials copied to clipboard!
        </Alert>
      )}
    </Box>
  );
};

export default OnboardingSuccess;

